import { infiniteQueryFetchNextPage } from '@repo/sdk'
import type { OrganizationSessionListItem } from '@repo/sdk/gen'
import { zV1OrganizationsListOrganizationSessionsData } from '@repo/sdk/zod'
import { PageLayout } from '@repo/ui/components/page-layout'
import { QueryBreadcrumbs } from '@repo/ui/components/query-breadcrumbs'
import { raise, zodNumberMaxValue } from '@repo/ui/utils'
import { useInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { SessionsEmpty } from '@/routes/(private)/(shell)/admin/sessions/-components/sessions-empty'
import { SessionsTable } from '@/routes/(private)/(shell)/admin/sessions/-components/sessions-table'
import { organizationSessionsQueryOptions } from '@/routes/(private)/(shell)/admin/sessions/-query/query-options'
import { usePageSize } from '@/stores/use-page-size'

export const Route = createFileRoute('/(private)/(shell)/admin/sessions/')({
	component: AdminSessions,
	staticData: {
		breadcrumb: 'admin-sessions:page_title'
	}
})

/**
 * Where the organization is signed in from, and how to close any of it.
 *
 * This is the surface that turns revocation into an action instead of an API
 * call: an administrator who suspects an access they do not recognise closes
 * it here, without writing to the reseller.
 *
 * **NO CHANNEL COLUMN.** `OrganizationSessionListItem` carries a `channel`
 * field reporting whether the realtime connection is live, and this page
 * deliberately does not read it: an administrator opening this page could not
 * explain what it meant, and its `unknown` reading — the realtime server
 * could not be asked — looked identical in weight to a real state on a row
 * they were deciding whether to close.
 *
 * **NO LOCATION COLUMN.** `OrganizationSessionListItem` has a `location`
 * field and this page ignores it: the server does not populate it on this
 * route, and a column empty on every row of every instance reads as a broken
 * feature rather than an unmade promise. The address is what the server does
 * know, so the address is what is shown.
 *
 * **NO ORGANIZATION-WIDE REVOCATION, AND NO EXPLANATION OF ITS ABSENCE.** It
 * is the platform operator's granularity, the server refuses it to anybody
 * holding a tenant session, and an administrator using it would disconnect
 * themselves along with everybody else. A disabled control or a note saying
 * why would advertise a capability this reader will never have.
 *
 * **THE PERIMETER IS THE ENDPOINT'S.** `list-organization-sessions` confines
 * the read to the caller's organization with a predicate of its own — the
 * `session` table is deliberately outside the row-level policies, because the
 * policy would need the organization the session is what establishes. There is
 * no organization field in the body and this screen must never grow a filter
 * standing in for one.
 *
 * **Infinite scroll, same as every other listing here.** The endpoint answers
 * offset pages rather than a cursor, but `offsetNextPageParam` walks page
 * numbers the same way `cursorNextPageParam` walks tokens — see the query
 * factory. There is no page number in the URL any more: nothing here is a
 * position anybody would bookmark or send to a colleague, the same reasoning
 * `admin/people` and `admin/audit` already read this list by.
 */
function AdminSessions() {
	const pageSize = usePageSize((state) => state.pageSize)

	const listQuery = {
		limit: Math.min(pageSize, MAX_PAGE_SIZE)
	}

	const sessions = useInfiniteQuery(
		organizationSessionsQueryOptions(listQuery)
	)
	const rows = sessions.data?.sessions ?? EMPTY_SESSIONS
	const total = sessions.data?.total ?? 0

	return (
		<PageLayout breadcrumb={<QueryBreadcrumbs />}>
			<div className='flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-4 pb-6 md:px-6'>
				<SessionsTable
					data={rows}
					loadMore={{
						hasMore: sessions.hasNextPage,
						loading: sessions.isFetchingNextPage,
						onLoadMore: () => infiniteQueryFetchNextPage(sessions)
					}}
					state={{
						empty: <SessionsEmpty />,
						error: sessions.isError,
						loading: sessions.isPending
					}}
					total={total}
				/>
			</div>
		</PageLayout>
	)
}

const EMPTY_SESSIONS: OrganizationSessionListItem[] = []

/**
 * The endpoint's own ceiling on a page, off the contract rather than retyped.
 * The rows-per-page preference is app-wide and its options are not bound to
 * this route's endpoint, so the two are reconciled here instead of by hoping
 * they stay in step.
 *
 * `raise` rather than a fallback: a schema that has lost its ceiling means the
 * contract changed shape, and a quietly substituted number would let this list
 * ask for pages the server refuses with a `400`.
 */
const MAX_PAGE_SIZE =
	zodNumberMaxValue(
		zV1OrganizationsListOrganizationSessionsData.shape.query.unwrap().shape
			.limit
	) ?? raise('list-organization-sessions lost its page-size ceiling')
